import { h } from "preact";
import { RoutableProps } from "preact-router";
import { useEffect, useState } from "preact/hooks";
import { resolvePath } from "../../api";
import { getProductTypeRevision, Label, ManufacturingConfiguration, ProductTypeRevision } from "../../api/product-api";
import { manufacturingConfigurations } from "../../App";
import { Snackbar, SnackbarShowCommand } from "../../components/Snackbar";
import { useCommand, useForceUpdate, useStepperIndex } from "../../hooks";
import { group } from "../../utils";
import "./styles.scss";
import { filterDefaultLabelsForRevision } from "../../data/filterDefaultLabelsForRevision";
import { countries } from "../../data/countries";
import { LabelPrint } from "../../components/LabelPrint";

export function PrintSerialNumber(props: RoutableProps & { type?: string }) {
    const [selectedLabel, setSelectedLabel] = useState(null as null | Label);
    const refreshPrinter = useCommand();
    const [batchCount, setBatchCount] = useState("1");
    const [eachCopyCount, setEachCopyCount] = useState("1");
    const [manufacturingConfiguration, setManufacturingConfiguration] = useState(
        null as null | ManufacturingConfiguration,
    );

    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const stepperIndex = useStepperIndex();
    const [revisions, setRevisions] = useState([] as ProductTypeRevision[]);
    const [selectedRevision, setSelectedRevision] = useState(null as ProductTypeRevision | null);
    const [selectedCountry, setSelectedCountry] = useState("");

    useEffect(() => {
        refreshPrinter.run();
    }, []);

    useEffect(() => {
        return () => {
            sessionStorage.setItem(`last-serial-number-${props.type}`, "");
        };
    });

    useEffect(() => {
        const config =
            Object.values(manufacturingConfigurations)
                .flat()
                .find(v => v?.productType.name === props.type) ?? null;

        if (!config) {
            setManufacturingConfiguration(null);
            return;
        }

        (async () => {
            const revisions = await Promise.all(
                config.productType.productTypeRevisions!.map(r => getProductTypeRevision(r.id)),
            );

            setRevisions(revisions);

            if (revisions.length === 1) {
                setSelectedRevision(revisions[0]);
                setSelectedCountry("");
                setSelectedLabel(null);
            }

            const currentRevision = revisions.find(v => v.id === config.productType.currentProductTypeRevision?.id);

            if (currentRevision) {
                setSelectedRevision(currentRevision);
                setSelectedCountry("");
                setSelectedLabel(null);
            }

            setManufacturingConfiguration(config);
        })();
    }, [props.type]);

    useEffect(() => {
        if (selectedRevision?.productTypeRevisionLabels) {
            const labels = selectedRevision.productTypeRevisionLabels.filter(
                v => v.label.metadata?.metadata.country === selectedCountry,
            );

            if (labels.length === 1) {
                setSelectedLabel(labels[0].label);
            } else {
                setSelectedLabel(null);
            }
        }
    }, [selectedCountry]);

    useEffect(() => {
        if (!selectedRevision) {
            setSelectedLabel(null);
            return;
        }

        const availableDefaultLabels = filterDefaultLabelsForRevision(selectedRevision).map(v => v.label);

        if (availableDefaultLabels.length === 1) {
            setSelectedLabel(availableDefaultLabels[0]);
        } else {
            setSelectedLabel(null);
        }
    }, [selectedRevision]);

    useEffect(() => {
        if (Math.floor(+batchCount) * Math.floor(+eachCopyCount) > 100) {
            let newBatchCount = Math.floor(+batchCount);

            while (newBatchCount > 1 && newBatchCount * Math.floor(+eachCopyCount) > 100) {
                newBatchCount--;
            }

            setBatchCount(newBatchCount.toString());

            showSnackbarCommand.run(
                "Maksymalna ilość naklejek jaką można wydrukować na raz to 100",
                "error",
                "error",
                3000,
            );
        }
    }, [batchCount]);

    useEffect(() => {
        if (Math.floor(+batchCount) * Math.floor(+eachCopyCount) > 100) {
            let newEachCopyCount = Math.floor(+eachCopyCount);

            while (newEachCopyCount > 1 && Math.floor(+batchCount) * newEachCopyCount > 100) {
                newEachCopyCount--;
            }

            setEachCopyCount(newEachCopyCount.toString());

            showSnackbarCommand.run(
                "Maksymalna ilość naklejek jaką można wydrukować na raz to 100",
                "error",
                "error",
                3000,
            );
        }
    }, [eachCopyCount]);

    if (!manufacturingConfiguration) {
        return null;
    }

    return (
        <div class="generate-serial-number-page">
            <h1>{manufacturingConfiguration.productType.name}</h1>
            <div>{manufacturingConfiguration.productType.brand}</div>
            {revisions.length <= 1
                ? null
                : [
                      <div class="stepper">
                          <div class="steps">
                              <div
                                  class={"step" + (selectedRevision ? " done success" : " current")}
                                  data-index={stepperIndex()}
                              >
                                  Wybierz rewizję
                              </div>
                          </div>
                      </div>,
                      <div class="paper" style="padding: 10px; margin-top: 10px;">
                          <h1>Rewizje</h1>
                          <div class="label-list list primary">
                              {revisions!.map(v => (
                                  <div
                                      class={"item" + (v === selectedRevision ? " selected" : "")}
                                      onClick={() => {
                                          setSelectedRevision(v);
                                          setSelectedCountry("");
                                          setSelectedLabel(null);
                                      }}
                                  >
                                      <div style="font-size: 16px;">{v.name}</div>
                                      <div style="flex: 1 1;" />
                                      <div>{v.changelog}</div>
                                      {v.id ===
                                      manufacturingConfiguration.productType.currentProductTypeRevision?.id ? (
                                          <div class="chip secondary" style="margin-left: 10px">
                                              Bieżąca rewizja
                                          </div>
                                      ) : null}
                                  </div>
                              ))}
                          </div>
                      </div>,
                  ]}
            {selectedRevision
                ? [
                      <div class="stepper">
                          <div class="steps">
                              <div
                                  class={"step" + (selectedLabel ? " done success" : " current")}
                                  data-index={stepperIndex()}
                              >
                                  Wybierz naklejkę
                              </div>
                          </div>
                      </div>,
                      <div class="paper" style="padding: 10px; margin-top: 10px; margin-bottom: 10px">
                          <h1>Naklejki</h1>
                          {selectedRevision.productTypeRevisionLabels!.filter(v =>
                              v.label.description.includes("(archived)"),
                          ).length > 0 && (
                              <div class="accordion" style="margin-top: 10px; margin-bottom: 10px;">
                                  <div>
                                      <input type="radio" name="accordion" />
                                      <input type="radio" name="accordion" />
                                      <div class="header">Zarchiwizowane</div>
                                      <div class="content" style="flex-direction: column">
                                          <div class="label-list list primary">
                                              {selectedRevision
                                                  .productTypeRevisionLabels!.filter(v =>
                                                      v.label.description.includes("(archived)"),
                                                  )
                                                  .map(v => (
                                                      <div
                                                          class={
                                                              "item" + (v.label === selectedLabel ? " selected" : "")
                                                          }
                                                          onClick={() => setSelectedLabel(v.label)}
                                                      >
                                                          <div style="font-size: 16px;">{v.label.name}</div>
                                                          <div style="flex: 1 1;" />
                                                          {v.label.description.includes("(archived)") ? (
                                                              <div class="chip warning">Zarchiwizowane</div>
                                                          ) : null}
                                                          {v.label.metadata &&
                                                          v.label.metadata.metadata["tape-type"] ? (
                                                              <div class="chip secondary" style="margin-left: 10px;">
                                                                  {v.label.metadata.metadata["tape-type"]}
                                                              </div>
                                                          ) : null}
                                                          {v.label.metadata &&
                                                          v.label.metadata.metadata["tape-width"] ? (
                                                              <div class="chip secondary" style="margin-left: 10px;">
                                                                  {v.label.metadata.metadata["tape-width"]}
                                                              </div>
                                                          ) : null}
                                                          {v.label.type === "group" && (
                                                              <div class="chip info" style="margin-left: 10px;">
                                                                  Zestaw naklejek
                                                              </div>
                                                          )}
                                                      </div>
                                                  ))}
                                          </div>
                                      </div>
                                  </div>
                              </div>
                          )}
                          <label>
                              Kraj:{" "}
                              <select
                                  style="margin-left: 5px"
                                  value={selectedCountry}
                                  onChange={e => {
                                      setSelectedCountry(e.currentTarget.value);
                                      setSelectedLabel(null);
                                  }}
                              >
                                  <option value="">Standardowe</option>
                                  {group(
                                      selectedRevision.productTypeRevisionLabels ?? [],
                                      v => v.label.metadata?.metadata.country ?? "",
                                  )
                                      .filter(v => v.key)
                                      .map(v => (
                                          <option value={v.key}>
                                              {countries[v.key as keyof typeof countries]} ({v.key})
                                          </option>
                                      ))}
                              </select>
                          </label>
                          <div style="margin-top: 10px" class="label-list list primary">
                              {selectedRevision
                                  .productTypeRevisionLabels!.filter(
                                      v =>
                                          selectedCountry === (v.label.metadata?.metadata.country ?? "") &&
                                          !v.label.description.includes("(archived)"),
                                  )
                                  .map(v => (
                                      <div
                                          class={"item" + (v.label === selectedLabel ? " selected" : "")}
                                          onClick={() => setSelectedLabel(v.label)}
                                      >
                                          <div style="font-size: 16px;">{v.label.name}</div>
                                          <div style="flex: 1 1;" />
                                          {v.label.metadata && v.label.metadata.metadata["tape-type"] ? (
                                              <div class="chip secondary" style="margin-left: 10px;">
                                                  {v.label.metadata.metadata["tape-type"]}
                                              </div>
                                          ) : null}
                                          {v.label.metadata && v.label.metadata.metadata["tape-width"] ? (
                                              <div class="chip secondary" style="margin-left: 10px;">
                                                  {v.label.metadata.metadata["tape-width"]}
                                              </div>
                                          ) : null}
                                          {v.label.type === "group" && (
                                              <div class="chip info" style="margin-left: 10px;">
                                                  Zestaw naklejek
                                              </div>
                                          )}
                                      </div>
                                  ))}
                          </div>
                          {selectedRevision.productTypeRevisionLabels!.length === 0 ? (
                              <div style="margin-top: 10px;">Brak naklejek dla tej rewizji</div>
                          ) : null}
                      </div>,
                  ]
                : null}
            {!selectedLabel
                ? null
                : [
                      [
                          <div class="stepper">
                              <div class="steps">
                                  <div
                                      class={"step" + (batchCount ? " done success" : " current")}
                                      data-index={stepperIndex()}
                                  >
                                      Podaj ilość kodów seryjnych do wygenerowania
                                  </div>
                              </div>
                          </div>,
                          <div class="create-serial">
                              <input
                                  type="number"
                                  value={batchCount}
                                  onChange={e => setBatchCount(e.currentTarget.value)}
                              />
                          </div>,
                          <div class="stepper">
                              <div class="steps">
                                  <div
                                      class={"step" + (eachCopyCount ? " done success" : " current")}
                                      data-index={stepperIndex()}
                                  >
                                      Podaj ilość kopii każdego kodu seryjnego (każda kopia będzie miała ten sam numer
                                      seryjny)
                                  </div>
                              </div>
                          </div>,
                          <div class="create-serial">
                              <input
                                  type="number"
                                  value={eachCopyCount}
                                  onChange={e => setEachCopyCount(e.currentTarget.value)}
                              />
                          </div>,
                      ],
                      batchCount && eachCopyCount && selectedRevision
                          ? [
                                <div class="stepper">
                                    <div class="steps">
                                        <div class="step current" data-index={stepperIndex()}>
                                            Wydrukuj naklejki (łączna ilość:{" "}
                                            {parseInt(batchCount, 10) * parseInt(eachCopyCount, 10)})
                                        </div>
                                    </div>
                                </div>,
                                <div class="print paper">
                                    <h1>Drukowanie naklejek</h1>
                                    <LabelPrint
                                        label={selectedLabel}
                                        copies={parseInt(batchCount, 10)}
                                        noAutoCut={true}
                                        doRefresh={refreshPrinter}
                                        eachProvideFieldsCopy={parseInt(eachCopyCount, 10)}
                                        productTypeRevision={selectedRevision}
                                    />
                                </div>,
                                // manufacturingConfiguration.otherData?.labelingStepsPreview ? (
                                //     <div class="paper" style="margin: 10px 0px; padding: 10px;">
                                //         <h1>Przkład naklejania</h1>
                                //         <div style="display: flex; justify-content: center;">
                                //             <img
                                //                 src={resolvePath(
                                //                     manufacturingConfiguration.otherData?.labelingStepsPreview,
                                //                 )}
                                //             />
                                //         </div>
                                //     </div>
                                // ) : null,
                            ]
                          : null,
                  ]}
            <Snackbar doShow={showSnackbarCommand} />
        </div>
    );
}
